/**
 * Card — geteilte Hülle für Settings-/Admin-Sektionen: cream Header (optionales
 * Icon + Titel + optionaler Count-Badge + optionaler Action-Slot), weißer Body.
 * `rounded-md` = 10px (Card-Radius). Extrahiert 2026-06-09 aus der lokalen
 * VocabCard (jetzt von Vokabular + Profil + Team geteilt — Rule-of-Three).
 *
 * Markup bewusst identisch zur Ex-VocabCard, damit die Migration null Drift ist.
 */
import type { ReactNode } from 'react';

type Props = {
  title: string;
  /** Icon links im Header (optional). */
  icon?: ReactNode;
  /** Zahl rechts im Header (z.B. Anzahl Einträge). */
  count?: number;
  /** Einheit für das aria-label des Count-Badges ("Produkte"/"Mitglieder"…). */
  countLabel?: string;
  /** Action-Slot rechts im Header (z.B. ein „Einladen"-Button). */
  action?: ReactNode;
  /** Body-Padding/Spacing — default `p-3` (dichte Listen); Formulare nutzen `p-5`. */
  bodyClassName?: string;
  className?: string;
  children: ReactNode;
};

export function Card({
  title,
  icon,
  count,
  countLabel,
  action,
  bodyClassName = 'p-3',
  className = '',
  children,
}: Props) {
  return (
    <section
      aria-label={title}
      className={`rounded-md border border-ink-100 bg-white shadow-soft overflow-hidden ${className}`}
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-brand-cream border-b border-ink-100">
        {icon && <span className="text-brand-green-600">{icon}</span>}
        {/* Titel + Count gruppiert (Count direkt am Titel, nicht orphaned am
            rechten Rand — Critique P2). Action wird via ml-auto rechts gehalten. */}
        <h2 className="text-xs font-semibold uppercase tracking-[0.04em] text-ink-500">
          {title}
        </h2>
        {count !== undefined && (
          <span
            className="rounded-full bg-brand-green-100 px-1.5 text-xs2 font-medium tabular-nums text-brand-green-700"
            aria-label={`${count} ${countLabel ?? 'Einträge'} insgesamt`}
          >
            {count}
          </span>
        )}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
